// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: pure helpers for the proactive engine's settings, kept apart from the
// store so Settings and the tests can use them without a server.

import type {
	KonvoProactiveSettings,
	KonvoProactiveSettingsPatch,
	KonvoProactiveTrigger,
	KonvoProactiveTriggerKind,
	KonvoProactiveTriggerStats,
	KonvoProactiveTuning,
} from '@app/features/konvo_connections/state/KonvoConnections';

/** The same bounds the server enforces; the sliders never offer anything it would refuse. */
export const PROACTIVE_LIMITS = {
	max_items_per_day: {min: 1, max: 30, fallback: 10},
	max_decisions_per_day: {min: 5, max: 100, fallback: 40},
	wrap_up_minutes: {min: 10, max: 120, fallback: 30},
} as const;

export type ProactiveLimitKey = keyof typeof PROACTIVE_LIMITS;

/** Settings lists the triggers from the most to the least time-sensitive. */
const TRIGGER_ORDER: ReadonlyArray<KonvoProactiveTriggerKind> = [
	'meeting_soon',
	'morning',
	'vip_mail',
	'task_overdue',
	'commitment_due',
	'chat_message',
	'waiting',
	'wrap_up',
];

export function clampProactiveLimit(key: ProactiveLimitKey, value: number): number {
	const {min, max, fallback} = PROACTIVE_LIMITS[key];
	if (!Number.isFinite(value)) return fallback;
	return Math.min(max, Math.max(min, Math.round(value)));
}

export function isClockTime(value: string): boolean {
	return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}

/** Known triggers in a fixed order; anything newer the server adds keeps its own order after them. */
export function orderProactiveTriggers(triggers: ReadonlyArray<KonvoProactiveTrigger>): Array<KonvoProactiveTrigger> {
	const rank = (kind: KonvoProactiveTriggerKind) => {
		const index = TRIGGER_ORDER.indexOf(kind);
		return index === -1 ? TRIGGER_ORDER.length : index;
	};
	return triggers
		.map((trigger, index) => ({trigger, index}))
		.sort((left, right) => rank(left.trigger.kind) - rank(right.trigger.kind) || left.index - right.index)
		.map(({trigger}) => trigger);
}

/** What the server will store for `patch`: top-level fields replace, triggers merge. */
export function applyProactivePatch(
	settings: KonvoProactiveSettings,
	patch: KonvoProactiveSettingsPatch,
): KonvoProactiveSettings {
	const {triggers, ...rest} = patch;
	const next: KonvoProactiveSettings = {...settings, ...rest, triggers: settings.triggers};
	if (triggers) {
		const merged = {...settings.triggers};
		for (const [kind, enabled] of Object.entries(triggers) as Array<[KonvoProactiveTriggerKind, boolean | undefined]>) {
			if (enabled !== undefined) merged[kind] = enabled;
		}
		next.triggers = merged;
	}
	return next;
}

/**
 * The line under a trigger in Settings. `decided` counts what the user has had
 * a chance to act on (used, dismissed, unwanted or left alone); items too new
 * to judge only count when nothing else has happened yet.
 */
export type ProactiveStatsSummary =
	| {kind: 'empty'; tuning: KonvoProactiveTuning}
	| {kind: 'pending'; pending: number; tuning: KonvoProactiveTuning}
	| {kind: 'counts'; used: number; decided: number; unwanted: number; tuning: KonvoProactiveTuning};

const count = (value: unknown): number =>
	typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

export function summarizeProactiveStats(
	stats: Partial<KonvoProactiveTriggerStats> | null | undefined,
): ProactiveStatsSummary {
	const tuning: KonvoProactiveTuning =
		stats?.tuning === 'selective' || stats?.tuning === 'strict' ? stats.tuning : 'normal';
	const used = count(stats?.used);
	const unwanted = count(stats?.bad);
	const decided = used + count(stats?.dismissed) + unwanted + count(stats?.maybe);
	if (decided > 0) return {kind: 'counts', used, decided, unwanted, tuning};
	const pending = count(stats?.pending);
	if (pending > 0) return {kind: 'pending', pending, tuning};
	return {kind: 'empty', tuning};
}

/** `konvo-feedback-2026-09-22.json`, in the user's local date. */
export function proactiveFeedbackFileName(now: Date): string {
	const pad = (value: number) => String(value).padStart(2, '0');
	return `konvo-feedback-${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}.json`;
}
